import type { SecondFactorEnrollmentStartedPayload } from '@Application/Security/Authentications/Actions/secondFactorEnrollmentStarted';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Message, Spinner } from '@verclam/ui-kit';

const centerStyle = css`
	display: flex;
	justify-content: center;
	padding: 1rem 0;
`;

const secretStyle = css`
	display: block;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.medium};
	word-break: break-all;
	background-color: ${theme.colors.surfaceMuted};
	border-radius: ${theme.radii.small};
	padding: ${theme.controls.padding};
	margin: 1rem 0;
`;

type Props = {
	readonly pairing: SecondFactorEnrollmentStartedPayload | null;
	readonly error: string | undefined;
	readonly isLoading: boolean;
	readonly onRetry: () => void;
	readonly onContinue: () => void;
};

/** Enrollment pairing step: QR code, secret for manual entry, and — the only error not toasted — a retry. */
const PairingStep: React.FC<Props> = ({ pairing, error, isLoading, onRetry, onContinue }) => {
	if (isLoading) {
		return (
			<div css={centerStyle}>
				<Spinner aria-label="Préparation de l’activation" />
			</div>
		);
	}

	if (error !== undefined) {
		return (
			<React.Fragment>
				<Message severity="error" text={error} />
				<Button type="button" label="Réessayer" onClick={onRetry} />
			</React.Fragment>
		);
	}

	if (pairing === null) {
		return null;
	}

	return (
		<React.Fragment>
			<p>
				Scannez ce code avec votre application d’authentification, ou recopiez le secret ci-dessous.
			</p>
			<div css={centerStyle}>
				<QRCodeSVG value={pairing.otpauthUri} />
			</div>
			<code css={secretStyle}>{pairing.secretBase32}</code>
			<Button type="button" label="J’ai scanné le code" onClick={onContinue} />
		</React.Fragment>
	);
};

export default PairingStep;
